import React, { FC, useEffect, useState } from 'react';
import * as S from './styles';
import { FormikValues, useFormik } from 'formik';
import dayjs, { Dayjs } from 'dayjs';
import { Radio, type RadioChangeEvent, Select, type SelectProps } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { cardSelect } from '@entities/card';
import { AppDispatch } from '@config/store';
import { getCardForms, getCardTemplates } from '@api/thunks/card';
import { authInfoSelect } from '@entities/auth';
import { ICreateRegForm } from '@api/services/cardApi/types';
import { updateFormRequest } from '@api/services/cardApi';
import { checkIsResponseStatus, isNotEmptyArray } from '@utils/functions';
import { validationSchema } from '@pages/crm/cards/ui/createFormModal/validationSchema';
import { ICardForm } from '@entities/card/types';

interface CreateFormProps {
    modalVisibilityToggler: () => void;
    cardFormId: React.Key;
}

export const UpdateCardFormModal: FC<CreateFormProps> = ({
    modalVisibilityToggler,
    cardFormId,
}) => {
    const [selectedValues, setSelectedValues] = useState<string>('');
    const { templates, forms } = useSelector(cardSelect);
    const { shop } = useSelector(authInfoSelect);
    const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(
        null
    );
    const [selectedForm, setSelectedForm] = useState<ICardForm | null>(null);
    const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(null);
    const dispatch = useDispatch<AppDispatch>();

    const initialValues = {
        title: selectedForm?.textAfter || '',
        subTitle: selectedForm?.textBefore || '',
        name: selectedForm?.name || '',
        startDate: selectedForm?.startDate || '',
        endDate: selectedForm?.endDate || '',
        issueCards: selectedForm?.issueCards || 0,
        cardBalance: selectedForm?.cardBalance || 0,
        cardBonus: selectedForm?.cardBonus || 0,
        template_id: selectedForm?.template_id?.toString() || '',
        state: selectedForm?.state || 0,
        clientState: selectedForm?.clientState || 0,
        cardState: selectedForm?.cardState || 0,
    };

    const onSubmit = async ({
        issueCards,
        endDate,
        startDate,
        name: formName,
        title,
        subTitle,
        state,
        clientState,
        cardState,
    }: FormikValues) => {
        if (!shop) return;
        const data: ICreateRegForm = {
            endDate: !endDate ? null : endDate,
            startDate: !startDate ? null : startDate,
            state,
            name: formName,
            textAfter: title,
            textBefore: subTitle,
            template_id: +selectedValues,
            cardState,
            clientState,
            issueCards: issueCards === 0 ? null : issueCards,
        };

        await updateFormRequest(shop.id, cardFormId, data).then(res => {
            if (checkIsResponseStatus(res.status)) {
                dispatch(getCardForms(shop.id));
            }
        });
        modalVisibilityToggler();
    };
    const {
        errors,
        touched,
        handleSubmit,
        setFieldValue,
        values,
        handleBlur,
        handleChange: handleFormicChange,
    } = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit,
    });

    const options: SelectProps['options'] = [
        ...(templates?.map(item => ({
            label: item.name,
            value: item.id.toString(),
        })) || []),
    ];

    const handleChange = (values: string) => {
        setSelectedValues(values);
    };

    const handleDateChange = (date: unknown) => {
        if (dayjs.isDayjs(date)) {
            setSelectedStartDate(date);
            setFieldValue('startDate', date.format());
        } else {
            setSelectedStartDate(null);
            setFieldValue('startDate', '');
        }
    };

    const handleEndDateChange = (date: unknown) => {
        if (dayjs.isDayjs(date)) {
            setSelectedEndDate(date);
            setFieldValue('endDate', date.format());
        } else {
            setSelectedStartDate(null);
            setFieldValue('endDate', '');
        }
    };
    const handleStateChange = (e: RadioChangeEvent) => {
        console.log('handleStateChange', e);
        setFieldValue('state', e.target.value);
    };

    const handleClientChange = (e: RadioChangeEvent) => {
        setFieldValue('clientState', e.target.value);
    };

    const handleCardChange = (e: RadioChangeEvent) => {
        setFieldValue('cardState', e.target.value);
    };

    useEffect(() => {
        if (isNotEmptyArray(forms) && cardFormId) {
            const form = forms.find(item => item.id === cardFormId);
            if (form) {
                setSelectedForm(form);
            }
        }
    }, [cardFormId, forms]);

    useEffect(() => {
        if (selectedForm) {
            setSelectedStartDate(
                selectedForm.startDate ? dayjs(selectedForm.startDate) : null
            );
            setSelectedEndDate(
                selectedForm.endDate ? dayjs(selectedForm.endDate) : null
            );

            setFieldValue('startDate', selectedForm.startDate || '');
            setFieldValue('endDate', selectedForm.endDate || '');

            const foundTemplate = templates?.find(
                t => t.id === selectedForm.template_id
            );

            if (foundTemplate) {
                setSelectedValues(foundTemplate.id.toString());
            }
        }
    }, [selectedForm, setFieldValue, templates]);

    useEffect(() => {
        if (shop) {
            dispatch(getCardTemplates(shop.id));
        }
    }, [dispatch, shop]);

    return (
        <S.Wrapper>
            <S.CloseBtn onClick={modalVisibilityToggler}>Закрыть</S.CloseBtn>
            <S.FormContainer onSubmit={handleSubmit}>
                <S.FormItemContainer>
                    <S.Label>Название формы</S.Label>
                    <S.FormItem
                        validateStatus={
                            touched.name && errors.name ? 'error' : ''
                        }
                        help={touched.name && errors.name ? errors.name : ''}
                    >
                        <S.Input
                            type="text"
                            name="name"
                            value={values.name}
                            required={!!errors.name && !!touched.name}
                            onChange={handleFormicChange}
                            onBlur={handleBlur}
                            $hasError={!!(errors.name && touched.name)}
                        />
                    </S.FormItem>
                </S.FormItemContainer>
                <S.FormItemContainer>
                    <S.Label>Заголовок формы</S.Label>
                    <S.FormItem
                        validateStatus={
                            touched.title && errors.title ? 'error' : ''
                        }
                        help={touched.title && errors.title ? errors.title : ''}
                    >
                        <S.Input
                            type="text"
                            name="title"
                            value={values.title}
                            required={!!errors.title && !!touched.title}
                            onChange={handleFormicChange}
                            onBlur={handleBlur}
                            $hasError={!!(errors.title && touched.title)}
                        />
                    </S.FormItem>
                </S.FormItemContainer>
                <S.FormItemContainer>
                    <S.Label>Подзаголовок формы</S.Label>
                    <S.FormItem
                        validateStatus={
                            touched.subTitle && errors.subTitle ? 'error' : ''
                        }
                        help={
                            touched.subTitle && errors.title
                                ? errors.subTitle
                                : ''
                        }
                    >
                        <S.Input
                            type="text"
                            name="subTitle"
                            value={values.subTitle}
                            required={!!errors.subTitle && !!touched.subTitle}
                            onChange={handleFormicChange}
                            onBlur={handleBlur}
                            $hasError={!!(errors.subTitle && touched.subTitle)}
                        />
                    </S.FormItem>
                </S.FormItemContainer>
                <S.FormItemContainer>
                    <S.FormItem
                        validateStatus={
                            touched.startDate && errors.startDate ? 'error' : ''
                        }
                        help={
                            errors.startDate && touched.startDate
                                ? errors.startDate
                                : ''
                        }
                    >
                        <S.Label>Дата начала действия</S.Label>
                        <S.StyledDatePicker
                            variant="outlined"
                            format="DD-MM-YYYY"
                            placeholder="DD-MM-YYYY"
                            value={selectedStartDate}
                            onChange={handleDateChange}
                            getPopupContainer={() => document.body}
                            inputReadOnly={true}
                            style={{
                                width: '100%',
                                height: '41px',
                                fontSize: '12px',
                                marginBottom: '8px',
                            }}
                        />
                    </S.FormItem>
                </S.FormItemContainer>
                <S.FormItemContainer>
                    <S.FormItem
                        validateStatus={
                            touched.endDate && errors.endDate ? 'error' : ''
                        }
                        help={
                            errors.endDate && touched.endDate
                                ? errors.endDate
                                : ''
                        }
                    >
                        <S.Label>Дата окончания действия</S.Label>
                        <S.StyledDatePicker
                            variant="outlined"
                            format="DD-MM-YYYY"
                            placeholder="DD-MM-YYYY"
                            value={selectedEndDate}
                            onChange={handleEndDateChange}
                            getPopupContainer={trigger =>
                                trigger.parentElement!
                            }
                            inputReadOnly={true}
                            style={{
                                width: '100%',
                                height: '41px',
                                fontSize: '12px',
                                marginBottom: '8px',
                            }}
                        />
                    </S.FormItem>
                </S.FormItemContainer>
                <S.FormItemContainer>
                    <div>Выбрать макеты</div>
                    <S.WrapSelect>
                        <Select
                            allowClear
                            style={{
                                width: '100%',
                                zIndex: 500,
                                height: '100%',
                            }}
                            placeholder="Выбрать макеты"
                            value={selectedValues}
                            onChange={handleChange}
                            options={options}
                        />
                    </S.WrapSelect>
                </S.FormItemContainer>
                <S.FormItemContainer>
                    <S.Label>Лимит карт</S.Label>
                    <S.FormItem
                        validateStatus={
                            touched.issueCards && errors.issueCards
                                ? 'error'
                                : ''
                        }
                        help={
                            touched.issueCards && errors.name
                                ? errors.issueCards
                                : ''
                        }
                    >
                        <S.Input
                            type="number"
                            name="issueCards"
                            value={values.issueCards}
                            required={!!errors.issueCards}
                            onChange={handleFormicChange}
                            onBlur={handleBlur}
                            $hasError={!!errors.issueCards}
                        />
                    </S.FormItem>
                </S.FormItemContainer>
                <S.FormItemContainer>
                    <span>Форма активна:</span>
                    <S.FormItem>
                        <Radio.Group
                            onChange={handleStateChange}
                            value={values.state}
                        >
                            <Radio value={1}>Активна</Radio>
                            <Radio value={0}>Не активна</Radio>
                        </Radio.Group>
                    </S.FormItem>
                </S.FormItemContainer>
                <S.FormItemContainer>
                    <span>Клиент активен после регистрации:</span>
                    <S.FormItem>
                        <Radio.Group
                            onChange={handleClientChange}
                            value={values.clientState}
                        >
                            <Radio value={1}>Активен</Radio>
                            <Radio value={0}>Блокирован</Radio>
                        </Radio.Group>
                    </S.FormItem>
                </S.FormItemContainer>
                <S.FormItemContainer>
                    <span>Карта активна после регистрации:</span>
                    <S.FormItem>
                        <Radio.Group
                            onChange={handleCardChange}
                            value={values.cardState}
                        >
                            <Radio value={1}>Активна</Radio>
                            <Radio value={0}>Блокирована</Radio>
                        </Radio.Group>
                    </S.FormItem>
                </S.FormItemContainer>
                <S.StyledButtonSend type="submit">
                    <span>Изменить</span>
                </S.StyledButtonSend>
            </S.FormContainer>
        </S.Wrapper>
    );
};
